import { IncrementalHtmlRewriter, rewriteHtml } from "@rewriters/html";
import { ScramjetClient } from "@client/index";
import { String, _URL } from "@/shared/snapshot";
import { createReferrerString } from "@/fetch/util";
import { blobSources, carrierBlob, needsCarrier } from "@client/carrier";

// In a frame outside the service worker (carrier.ts) the parser would load a written script
// from the frame itself, which misses the proxy. Such scripts are written with a placeholder
// address; the output is held from that script on, until the script has been fetched through
// the carrier, and then written on with the blob: address in its place. Everything is written
// in the order the page wrote it, only later; close() waits for it too.
const DEFERRED = "about:blank#scramjet-deferred-";
const DEFERRED_RE = /about:blank#scramjet-deferred-\d+/;
let deferredCounter = 0;

type HeldWrites = {
	owner: ScramjetClient;
	sources: Map<string, string>;
	queue: string[];
	busy: boolean;
	closing: boolean;
	dead: boolean;
};

export default function (client: ScramjetClient, _self: Self) {
	const held = new WeakMap<Document, HeldWrites>();

	function nativeWrite(document: Document, html: string) {
		return client.natives.call("Document.prototype.write", document, html);
	}

	function resetDocumentWriter(document: Document) {
		client.box.writeRewriters.delete(document);
	}

	function getDocumentWriter(document: Document) {
		let writer = client.box.writeRewriters.get(document);
		if (!writer) {
			const owner = client.box.documents.get(document) || client;
			writer = new IncrementalHtmlRewriter(client.context, client.meta, {
				loadScripts: false,
				inline: true,
				source: client.url.href,
				apisource: "Document.prototype.write",
				replaceScriptSrc: needsCarrier(owner)
					? (proxied: string) => {
							let state = held.get(document);
							if (!state) {
								state = {
									owner,
									sources: new Map(),
									queue: [],
									busy: false,
									closing: false,
									dead: false,
								};
								held.set(document, state);
							}
							const key = DEFERRED + ++deferredCounter;
							state.sources.set(key, proxied);

							return key;
						}
					: undefined,
			});
			client.box.writeRewriters.set(document, writer);
		}

		return writer;
	}

	// the placeholders in `html` back to the proxied addresses (loaded from the frame after all)
	function restoreSources(state: HeldWrites, html: string) {
		return html.replace(
			new RegExp(DEFERRED_RE.source, "g"),
			(key) => state.sources.get(key) || key
		);
	}

	function pump(document: Document, state: HeldWrites) {
		while (!state.busy && state.queue.length > 0) {
			const text = state.queue.join("");
			state.queue.length = 0;
			const match = DEFERRED_RE.exec(text);
			if (!match) {
				nativeWrite(document, text);
				continue;
			}
			const key = match[0];
			let start = text.lastIndexOf("<script", match.index);
			if (start === -1) start = match.index;
			if (start > 0) nativeWrite(document, text.slice(0, start));
			state.queue.push(text.slice(start));
			state.busy = true;

			const proxied = state.sources.get(key) || "";
			state.sources.delete(key);
			const resume = (address: string) => {
				if (state.dead) return;
				state.queue[0] = state.queue[0].replace(key, address);
				state.busy = false;
				pump(document, state);
			};
			const loading = carrierBlob(state.owner, proxied, "script");
			if (loading)
				loading.then((blob) => {
					if (blob) blobSources.set(blob, proxied);
					resume(blob || proxied);
				});
			else resume(proxied);
		}
		if (!state.busy && state.queue.length === 0 && state.closing) {
			state.closing = false;
			client.natives.call("Document.prototype.close", document);
		}
	}

	function write(document: Document, html: string) {
		const state = held.get(document);
		if (!state) return nativeWrite(document, html);
		// written by a script the parser is running: it belongs where the parser is now
		let current = null;
		try {
			current = client.descriptors.get("Document.prototype.currentScript", document);
		} catch {}
		if (current) return nativeWrite(document, restoreSources(state, html));

		state.queue.push(html);
		pump(document, state);
	}

	client.Proxy("Document.prototype.open", {
		apply(ctx) {
			resetDocumentWriter(ctx.this);
			const state = held.get(ctx.this);
			if (state) {
				state.dead = true;
				held.delete(ctx.this);
			}
		},
	});

	client.Proxy("Document.prototype.write", {
		apply(ctx) {
			const writer = getDocumentWriter(ctx.this);
			ctx.return(write(ctx.this, writer.write(ctx.args.join(""))));
		},
	});

	client.Proxy("Document.prototype.writeln", {
		apply(ctx) {
			const writer = getDocumentWriter(ctx.this);
			ctx.return(write(ctx.this, writer.write(ctx.args.join("") + "\n")));
		},
	});

	client.Proxy("Document.prototype.close", {
		apply(ctx) {
			const writer = client.box.writeRewriters.get(ctx.this);
			if (!writer) {
				return;
			}

			try {
				const remaining = writer.end();
				if (remaining) {
					write(ctx.this, remaining);
				}
				const state = held.get(ctx.this);
				if (state && (state.busy || state.queue.length > 0)) {
					// closed once everything held back is written
					state.closing = true;
					ctx.return(undefined);
				}
			} finally {
				resetDocumentWriter(ctx.this);
			}
		},
	});

	// a static function (Document.parseHTMLUnsafe); the prototype name never existed
	client.Proxy(["Document.parseHTMLUnsafe", "Document.prototype.parseHTMLUnsafe"], {
		apply(ctx) {
			const html = String(ctx.args[0]);
			ctx.args[0] = rewriteHtml(html, client.context, client.meta, {
				loadScripts: false,
				inline: true,
				source: client.url.href,
				apisource: "Document.prototype.parseHTMLUnsafe",
			});
		},
	});

	client.Trap("Document.prototype.domain", {
		get() {
			return client.url.hostname;
		},
		set() {
			return false;
		},
	});

	client.Trap("Document.prototype.documentURI", {
		get() {
			return client.url.href;
		},
		set() {
			return false;
		},
	});

	client.Trap("Document.prototype.URL", {
		get() {
			return client.url.href;
		},
		set() {
			return false;
		},
	});

	client.Trap("Document.prototype.referrer", {
		get() {
			if (!client.history) return "";
			if (client.history.length < 2) return "";
			const lastState = client.history[client.history.length - 2];
			const referrerURL = new _URL(lastState.url);
			return createReferrerString(
				referrerURL,
				client.url,
				lastState.refererPolicy
			);
		},
	});

	// Selectors on rewritten attributes: client/dom/selectors.ts.
}
